import React, { useEffect, useRef, useState, useCallback } from 'react';
import './VideoPlayer.css';

export default function RutubePlayer({ videoId, platform = 'rutube', oid, hash, initialState, roomId, emit, on }) {
  const iframeRef = useRef(null);
  const [syncStatus, setSyncStatus] = useState('');
  const [isPlaying, setIsPlaying] = useState(initialState?.playing ?? true);
  const [currentTime, setCurrentTime] = useState(initialState?.currentTime || 0);
  const [duration, setDuration] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);

  // Начинаем в режиме suppress: не отправляем события пока плеер не получил
  // первичную синхронизацию (иначе iframe стартует с t=0 и рассылает всем неверную позицию)
  const suppressRemoteRef = useRef(true);
  const lastServerTimeRef = useRef(initialState?.currentTime || 0);
  const lastCorrectionTimeRef = useRef(0);
  const lastPlayPauseEmitRef = useRef(0);
  const currentTimeRef = useRef(initialState?.currentTime || 0);
  // prevTimeRef стартует с 0, а не с initialState.currentTime:
  // iframe-плеер всегда начинает репортить время с ~0 при загрузке,
  // и если prevTimeRef = 300, первый diff будет 300 > 2.5 → ложный emit video_seek на всех.
  const prevTimeRef = useRef(0);
  const prevPlayingRef = useRef(initialState?.playing ?? false);
  // Флаг: игнорируем seek-детекцию пока плеер не репортил время хотя бы раз
  const playerReportedRef = useRef(false);
  // Последнее состояние от сервера (video_state), пришедшее до готовности плеера
  const pendingStateRef = useRef(null);
  // Флаг: начальная синхронизация уже применена (не применять повторно)
  const initialSyncAppliedRef = useRef(false);

  const playPauseDebounceRef = useRef(null);
  
  const showSync = useCallback((msg) => {
    setSyncStatus(msg);
    setTimeout(() => setSyncStatus(''), 2000);
  }, []);

  const sendToPlayer = useCallback((type, data = {}) => {
    if (!iframeRef.current?.contentWindow) return;
    const targetOrigin = platform === 'vk' ? 'https://vkvideo.ru' : 'https://rutube.ru';
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ type, data }),
      targetOrigin
    );
  }, [platform]);

  // ==================== MESSAGE FROM IFRAME ====================
  useEffect(() => {
    const handleMessage = (e) => {
      const origin = String(e.origin);
      const isRutube = origin.includes('rutube.ru');
      const isVK = origin.includes('vkvideo.ru') || origin.includes('vk.com');
      if (!isRutube && !isVK) return;

      let data;
      try {
        data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch { return; }

      const { type, data: p } = data || {};

      // Ready
      if (type === 'player:ready' || (isVK && data.type === 'inited')) {
        setPlayerReady(true);
      }

      // Duration
      if (type === 'player:durationChange' && p?.duration) setDuration(p.duration);
      if (isVK && p?.duration) setDuration(p.duration);

      // Current time
      const timeVal = p?.time ?? p?.currentTime;
      if (timeVal !== undefined) {
        const prev = prevTimeRef.current;
        const diff = Math.abs(timeVal - prev);

        setCurrentTime(timeVal);
        currentTimeRef.current = timeVal;
        prevTimeRef.current = timeVal;

        // Seek детекция — пропускаем первый апдейт (плеер стартует с 0,
        // что вызвало бы ложный seek если initialState.currentTime > 0)
        if (playerReportedRef.current && !suppressRemoteRef.current && diff > 2.5) {
          const now = Date.now();
          if (now - lastPlayPauseEmitRef.current > 800) {
            lastPlayPauseEmitRef.current = now;
            emit('video_seek', { roomId, currentTime: timeVal });
            showSync('⏩ Вы перемотали');
          }
        }
        playerReportedRef.current = true;
      }

      // Play / Pause — только если вкладка видима и не suppress
      if (!suppressRemoteRef.current && document.visibilityState === 'visible') {
        const isNowPlaying = type === 'player:play' || type === 'started' || type === 'resumed' ||
          (type === 'player:changeState' && (p?.state === 1 || p?.isPlaying === true));

        const isNowPaused = type === 'player:pause' || type === 'paused' ||
          (type === 'player:changeState' && (p?.state === 2 || p?.isPlaying === false));

        const now = Date.now();

        if (isNowPlaying && !prevPlayingRef.current) {
          prevPlayingRef.current = true;
          setIsPlaying(true);
          if (playPauseDebounceRef.current) clearTimeout(playPauseDebounceRef.current);
          playPauseDebounceRef.current = setTimeout(() => {
            lastPlayPauseEmitRef.current = Date.now();
            emit('video_play', { roomId, currentTime: currentTimeRef.current });
            showSync('▶ Вы запустили');
          }, 300);
        }

        if (isNowPaused && prevPlayingRef.current) {
          prevPlayingRef.current = false;
          setIsPlaying(false);
          if (playPauseDebounceRef.current) clearTimeout(playPauseDebounceRef.current);
          playPauseDebounceRef.current = setTimeout(() => {
            lastPlayPauseEmitRef.current = Date.now();
            emit('video_pause', { roomId, currentTime: currentTimeRef.current });
            showSync('⏸ Вы поставили паузу');
          }, 300);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [emit, roomId, showSync]);

  // ==================== POLLING ====================
  useEffect(() => {
    const interval = setInterval(() => {
      sendToPlayer('player:getCurrentTime', {});
      sendToPlayer('player:getState', {});
    }, 1500);
    return () => clearInterval(interval);
  }, [sendToPlayer]);

  // ==================== НАЧАЛЬНАЯ СИНХРОНИЗАЦИЯ ПРИ ГОТОВНОСТИ ПЛЕЕРА ====================
  // Если video_state пришёл до того, как плеер был готов, postMessage были проигнорированы.
  // Когда плеер становится готовым — применяем последнее сохранённое состояние.
  useEffect(() => {
    if (!playerReady || initialSyncAppliedRef.current) return;
    initialSyncAppliedRef.current = true;

    const pending = pendingStateRef.current;
    const time = pending?.time ?? currentTimeRef.current;
    const playing = pending?.playing ?? prevPlayingRef.current;

    suppressRemoteRef.current = true;
    playerReportedRef.current = false;

    sendToPlayer('player:setCurrentTime', { time });
    if (playing) {
      sendToPlayer('player:play', {});
    } else {
      sendToPlayer('player:pause', {});
    }

    setTimeout(() => { suppressRemoteRef.current = false; }, 1800);
  }, [playerReady, sendToPlayer]);

  // ==================== СЕРВЕРНЫЕ СОБЫТИЯ ====================
  useEffect(() => {
    const offPlay = on('video_play', ({ currentTime: t, by }) => {
      suppressRemoteRef.current = true;
      lastServerTimeRef.current = t;
      prevPlayingRef.current = true;
      setIsPlaying(true);
      setCurrentTime(t);
      currentTimeRef.current = t;
      sendToPlayer('player:setCurrentTime', { time: t });
      sendToPlayer('player:play', {});
      showSync(`▶ ${by || 'Участник'} запустил`);
      setTimeout(() => { suppressRemoteRef.current = false; }, 1200);
    });

    const offPause = on('video_pause', ({ currentTime: t, by }) => {
      suppressRemoteRef.current = true;
      lastServerTimeRef.current = t;
      prevPlayingRef.current = false;
      setIsPlaying(false);
      setCurrentTime(t);
      currentTimeRef.current = t;
      sendToPlayer('player:setCurrentTime', { time: t });
      sendToPlayer('player:pause', {});
      showSync(`⏸ ${by || 'Участник'} поставил паузу`);
      setTimeout(() => { suppressRemoteRef.current = false; }, 1200);
    });

    const offSeek = on('video_seek', ({ currentTime: t, by }) => {
      suppressRemoteRef.current = true;
      lastServerTimeRef.current = t;
      setCurrentTime(t);
      currentTimeRef.current = t;
      prevTimeRef.current = t;
      sendToPlayer('player:setCurrentTime', { time: t });
      showSync(`⏩ ${by || 'Участник'} перемотал`);
      setTimeout(() => { suppressRemoteRef.current = false; }, 1000);
    });

    const offVideoState = on('video_state', (state) => {
      if (!state) return;
      const { currentTime: serverTime = 0, isPlaying: serverIsPlaying = false } = state;

      // Всегда сохраняем актуальное состояние — применим когда плеер будет готов
      pendingStateRef.current = { time: serverTime, playing: serverIsPlaying };

      // Если дрейф небольшой — не делаем перемотку, чтобы не прерывать воспроизведение
      // при возврате на вкладку (плеер уже на правильной позиции).
      const drift = Math.abs(serverTime - currentTimeRef.current);
      const needsSeek = drift > 3;
      const wasPlaying = prevPlayingRef.current;

      suppressRemoteRef.current = true;
      lastServerTimeRef.current = serverTime;
      prevPlayingRef.current = serverIsPlaying;
      setIsPlaying(serverIsPlaying);

      if (needsSeek) {
        // Сбрасываем флаг — после команды перемотки плеер снова начнёт репортить с ~0,
        // первый апдейт нужно пропустить чтобы не словить ложный seek
        playerReportedRef.current = false;
        currentTimeRef.current = serverTime;
        prevTimeRef.current = serverTime;
        setCurrentTime(serverTime);
        sendToPlayer('player:setCurrentTime', { time: serverTime });
      }

      // Команду play/pause отправляем только если изменилось состояние воспроизведения
      if (serverIsPlaying && !wasPlaying) {
        sendToPlayer('player:play', {});
      } else if (!serverIsPlaying && wasPlaying) {
        sendToPlayer('player:pause', {});
      }

      showSync('🔄 Синхронизировано');

      setTimeout(() => {
        suppressRemoteRef.current = false;
      }, needsSeek ? 1800 : 300);
    });

    return () => {
      offPlay();
      offPause();
      offSeek();
      offVideoState();
    };
  }, [on, sendToPlayer, showSync]);

  const vkSrc = hash
    ? `https://vk.com/video_ext.php?oid=${oid}&id=${videoId}&hash=${hash}`
    : `https://vk.com/video_ext.php?oid=${oid}&id=${videoId}`;

  // Передаём стартовое время в URL чтобы iframe сразу начинал с нужной позиции
  // (postMessage-команды на перемотку могут прийти раньше готовности плеера)
  const startSeconds = Math.floor(initialState?.currentTime || 0);
  const embedSrc = platform === 'vk'
    ? vkSrc
    : `https://rutube.ru/play/embed/${videoId}${startSeconds > 0 ? `?t=${startSeconds}` : ''}`;

  return (
    <div className="video-player">
      <div className="video-wrapper">
        <iframe
          ref={iframeRef}
          src={embedSrc}
          title="Rutube Player"
          frameBorder="0"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
          className="video-iframe"
        />
        {syncStatus && <div className="sync-toast">{syncStatus}</div>}
        {!playerReady && (
          <div className="video-overlay">
            <div className="loading-spinner" />
            <span>Загрузка плеера...</span>
          </div>
        )}
      </div>

      <div className="video-controls">
        <div className="progress-bar" onClick={(e) => {
          if (!duration) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          const t = ratio * duration;
          currentTimeRef.current = t;
          setCurrentTime(t);
          sendToPlayer('player:setCurrentTime', { time: t });
          emit('video_seek', { roomId, currentTime: t });
          showSync('⏩ Вы перемотали');
        }}>
          <div className="progress-fill" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
        </div>
        <div className="controls-row">
          <button className="ctrl-btn" onClick={() => {
            if (isPlaying) {
              sendToPlayer('player:pause', {});
              emit('video_pause', { roomId, currentTime: currentTimeRef.current });
            } else {
              sendToPlayer('player:play', {});
              emit('video_play', { roomId, currentTime: currentTimeRef.current });
            }
          }}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <span className="time-display">
            {Math.floor(currentTime / 60)}:{(Math.floor(currentTime % 60)).toString().padStart(2, '0')} / 
            {Math.floor(duration / 60)}:{(Math.floor(duration % 60)).toString().padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>
  );
}